<template>
  <div
    v-if="offer.show"
    class="fixed bottom-4 left-1/2 -translate-x-1/2 z-50 w-[calc(100%-2rem)] max-w-md rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 shadow-xl px-4 py-3 flex items-center gap-3"
    data-install-offer>
    <span class="material-symbols-outlined text-blue-600 dark:text-blue-400" aria-hidden="true">smartphone</span>
    <p class="flex-1 text-sm text-gray-800 dark:text-gray-200">{{ t('installOffer.text') }}</p>
    <a
      :href="offer.url"
      target="_blank"
      rel="noopener"
      class="px-3 py-1.5 rounded-lg bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium"
      data-install-offer-action>
      {{ t('installOffer.button') }}
    </a>
    <button
      type="button"
      :aria-label="t('installOffer.dismiss')"
      class="text-gray-400 hover:text-gray-500"
      data-install-offer-dismiss
      @click="dismiss">
      <span class="material-symbols-outlined">close</span>
    </button>
  </div>
</template>

<script setup lang="ts">
/**
 * The install offer on app.torik.ai (the owner's ask, 2026-10-05): a phone
 * browser gets one quiet line and two doors — Install, and Not now, which
 * stays dismissed (`torik.installOfferDismissed`) rather than nagging.
 *
 * Self-hides everywhere else; the whole rule is `utils/installOffer.ts` (pure,
 * under test), and where the doors lead today — both TESTING doors, the stores
 * have no public listing yet — is `config/appStores.ts`.
 */
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { getRuntimeConfig, isCapacitor } from '../config/runtime';
import { APP_STORE_LINKS } from '../config/appStores';
import { installOfferFor, type InstallOffer } from '../utils/installOffer';

const { t } = useI18n();

const DISMISSED_KEY = 'torik.installOfferDismissed';

const dismissed = ref(readDismissed());

const offer = computed<InstallOffer>(() =>
  dismissed.value
    ? { show: false, platform: null, url: '' }
    : installOfferFor({
        mode: getRuntimeConfig().mode,
        nativeApp: isCapacitor(),
        userAgent: typeof navigator === 'undefined' ? '' : navigator.userAgent,
        links: APP_STORE_LINKS,
      }),
);

function readDismissed(): boolean {
  try {
    return localStorage.getItem(DISMISSED_KEY) === '1';
  } catch {
    return false; // storage unavailable (private mode) — offer anyway
  }
}

function dismiss() {
  dismissed.value = true;
  try {
    localStorage.setItem(DISMISSED_KEY, '1');
  } catch {
    /* nothing to remember it in — it stays dismissed for this page */
  }
}
</script>
